import { LogLevelNames } from '@vp/ubik-fragment-types'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type LogMethod = (message: any, ...params: any[]) => void
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ErrorLogMethod = (message: any | Error, ...params: any[]) => void

export interface Logger {
  debug: LogMethod
  info: LogMethod
  log: LogMethod
  warn: LogMethod
  error: ErrorLogMethod
}

export enum LogLevel {
  TRACE = 0,
  DEBUG = 1,
  INFO = 2,
  WARN = 3,
  ERROR = 4,
}

export const convertLogLevel = (logLevel: string | number): LogLevel => {
  if (typeof logLevel === 'number') {
    if (logLevel in LogLevel) {
      return logLevel as LogLevel
    } else {
      console.log(`ubik-logging: Invalid log level number: ${logLevel}`)
      return LogLevel.INFO
    }
  } else if (typeof logLevel === 'string') {
    const normalizedLogLevel = logLevel.toUpperCase()
    if (normalizedLogLevel in LogLevel) {
      return LogLevel[normalizedLogLevel as keyof typeof LogLevel]
    } else {
      console.log(`ubik-logging: Invalid log level string: ${logLevel}`)
      return LogLevel.INFO
    }
  } else {
    console.log(`ubik-logging: Invalid log level type: ${typeof logLevel}`)
    return LogLevel.INFO
  }
}

const convertLogLevelNameToLogLevel = (logLevel: LogLevelNames): LogLevel => {
  switch (logLevel) {
    case 'trace':
      return LogLevel.TRACE
    case 'debug':
      return LogLevel.DEBUG
    case 'info':
      return LogLevel.INFO
    case 'warn':
      return LogLevel.WARN
    case 'error':
      return LogLevel.ERROR
    default:
      console.log(`ubik-logging: Invalid log level name: ${logLevel}`)
      return defaultLogLevel
  }
}

const defaultLogLevel = LogLevel.INFO

export class FragmentLogger implements Logger {
  protected logger: Logger
  protected name: string = 'default-logger-name'
  protected level: LogLevel = defaultLogLevel
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected context: Record<string, any> = {}

  constructor(logger: Logger) {
    this.logger = logger
  }

  setLoggerLevel(level: LogLevelNames): void {
    this.level = convertLogLevelNameToLogLevel(level)
  }

  setLoggerName(name: string): void {
    this.name = name
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
  postLog(_level: LogLevel, _message: any | Error, _flattenedParams: Record<string, any> | any[]): void {
    // no-op
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  flattenParams(...params: any[]): Record<string, any> {
    let flattenedParams = params
    try {
      const paramsTrasformed = params.map((param, index) => {
        if (param instanceof Error) {
          return {
            [`errorMessage-${index}`]: param.message,
            [`errorStackTrace-${index}`]: param.stack,
          }
        }
        if (Array.isArray(param) || typeof param === 'string' || typeof param === 'number' || param == null || param === undefined) {
          return { [`param-${index}`]: param }
        }
        if (typeof param === 'object') {
          return param
        }
        return { [`param-${index}`]: param }
      })
      flattenedParams = Object.assign({}, { name: this.name }, this.context, ...paramsTrasformed)
    } catch {
      // NOOP
    }
    return flattenedParams
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  debug(message: any, ...params: any[]): void {
    const flattenedParams = this.flattenParams(...params)
    if (this.level <= LogLevel.DEBUG) this.logger.debug(message, flattenedParams)
    this.postLog(LogLevel.DEBUG, message, flattenedParams)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  log(message: any, ...params: any[]): void {
    const flattenedParams = this.flattenParams(...params)
    if (this.level <= LogLevel.DEBUG) this.logger.log(message, flattenedParams)
    this.postLog(LogLevel.DEBUG, message, flattenedParams)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  info(message: any, ...params: any[]): void {
    const flattenedParams = this.flattenParams(...params)
    if (this.level <= LogLevel.INFO) this.logger.info(message, flattenedParams)
    this.postLog(LogLevel.INFO, message, flattenedParams)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  warn(message: any, ...params: any[]): void {
    const flattenedParams = this.flattenParams(...params)
    if (this.level <= LogLevel.WARN) this.logger.warn(message, flattenedParams)
    this.postLog(LogLevel.WARN, message, flattenedParams)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error(message: any | Error, ...params: any[]): void {
    const flattenedParams = this.flattenParams(...params)
    if (this.level <= LogLevel.ERROR) this.logger.error(message, flattenedParams)
    this.postLog(LogLevel.ERROR, message, flattenedParams)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setContext(context: Record<string, any>): void {
    this.context = context
  }
}
